import { ReceiptText } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// IT Services / Web Projects / Marketing & Advertising — Rate Cards Page
//
// Service rate card management with role-based rates, seniority multipliers,
// currency overrides, and project-specific overrides
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Calculator, Plus, Edit, Trash2, Check } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Modal } from '@/shared/components/ui/Modal';
import {
  useRateCards,
  useActiveRateCard,
  useRateCardMutations,
} from '../hooks/useServicesProjectsApi';
import { localToday } from '@/shared/utils/utils';

const SENIORITY_LEVELS = ['Junior', 'Mid', 'Senior', 'Lead', 'Principal'];
const ROLE_TYPES = ['Developer', 'Designer', 'Project Manager', 'Business Analyst', 'QA Engineer', 'DevOps Engineer', 'Architect', 'Strategist', 'Creative Director', 'Account Manager'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];

interface RateCardEntry {
  key: string;
  role: string;
  seniority: string;
  hourlyRateMinor: number;
  billingRateMinor: number;
  currency: string;
}

const emptyEntry = (): RateCardEntry => ({
  key: Math.random().toString(36).slice(2),
  role: '',
  seniority: 'Mid',
  hourlyRateMinor: 0,
  billingRateMinor: 0,
  currency: 'INR',
});

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

export function RateCardsPage() {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    version: '1.0',
    effectiveFrom: localToday(),
    effectiveTo: '',
    entries: [emptyEntry()] as RateCardEntry[],
  });
  const [saving, setSaving] = useState(false);

  const { data: rateCards = [], isLoading } = useRateCards();
  const { data: activeRateCard } = useActiveRateCard();
  const { create, update, activate } = useRateCardMutations();

  const resetForm = useCallback(() => {
    setFormData({
      code: '',
      name: '',
      description: '',
      version: '1.0',
      effectiveFrom: localToday(),
      effectiveTo: '',
      entries: [emptyEntry()],
    });
    setEditingId(null);
    setCreating(false);
  }, []);

  const setEntry = (key: string, patch: Partial<RateCardEntry>) =>
    setFormData(f => ({ ...f, entries: f.entries.map(e => e.key === key ? { ...e, ...patch } : e) }));

  const addEntry = () => setFormData(f => ({ ...f, entries: [...f.entries, emptyEntry()] }));
  const removeEntry = (key: string) => setFormData(f => ({ ...f, entries: f.entries.filter(e => e.key !== key) }));

  const handleCreate = () => {
    resetForm();
    setCreating(true);
  };

  const handleEdit = (rc: any) => {
    if (rc.status !== 'draft') {
      return toast.error('Only draft rate cards can be modified');
    }
    setEditingId(rc._id);
    setFormData({
      code: rc.code,
      name: rc.name,
      description: rc.description || '',
      version: rc.version || '1.0',
      effectiveFrom: rc.effectiveFrom ? new Date(rc.effectiveFrom).toISOString().split('T')[0] : '',
      effectiveTo: rc.effectiveTo ? new Date(rc.effectiveTo).toISOString().split('T')[0] : '',
      entries: (rc.entries || []).map((e: any) => ({
        key: e._id || Math.random().toString(36).slice(2),
        role: e.role,
        seniority: e.seniority,
        hourlyRateMinor: e.hourlyRateMinor || 0,
        billingRateMinor: e.billingRateMinor || 0,
        currency: e.currency || 'INR',
      })),
    });
    setCreating(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.code.trim() || !formData.name.trim()) {
      return toast.error('Code and Name are required');
    }
    if (formData.entries.some(e => !e.role || !e.seniority)) {
      return toast.error('All entries must have a role and seniority');
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        effectiveFrom: formData.effectiveFrom ? new Date(formData.effectiveFrom).toISOString() : null,
        effectiveTo: formData.effectiveTo ? new Date(formData.effectiveTo).toISOString() : null,
        entries: formData.entries.map(({ key: _k, ...e }) => e),
      };

      if (editingId) {
        const existing = rateCards.find((r: any) => r._id === editingId);
        if (existing && existing.status !== 'draft') {
          setSaving(false);
          return toast.error('Only draft rate cards can be modified');
        }
        await update.mutateAsync({ id: editingId, payload });
        toast.success('Rate card updated');
      } else {
        await create.mutateAsync(payload);
        toast.success('Rate card created');
      }
      resetForm();
    } catch (err) {
      toast.error(errorText(err, 'Failed to save rate card'));
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id: string, code: string) => {
    if (!window.confirm(`Activate rate card ${code}? This will archive the currently active rate card.`)) return;
    try {
      await activate.mutateAsync(id);
      toast.success('Rate card activated');
    } catch (err) {
      toast.error(errorText(err, 'Failed to activate rate card'));
    }
  };

  if (isLoading) return <div className="p-6"><Spinner /></div>;

  return (
    <div className="space-y-6 pb-16">
      {/* Header */}
      <PageHeader
        icon={ReceiptText}
        eyebrow="Projects & Services"
        title="Service Rate Cards"
        description={<>Define tiered rate cards by role and seniority. Only one rate card can be active at a time.</>}
        actions={
          <><Button onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-1" /> New Rate Card
                </Button></>
        }
      />

      {/* Active Rate Card Banner */}
      {activeRateCard && (
        <div className="bg-emerald-50 border border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-800 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator className="h-5 w-5 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-800 dark:text-emerald-200">Active Rate Card</p>
              <p className="text-sm text-emerald-600 dark:text-emerald-400">
                {activeRateCard.code} — {activeRateCard.name} (v{activeRateCard.version})
              </p>
            </div>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">Active</Badge>
        </div>
      )}

      {/* Rate Cards List */}
      {rateCards.length === 0 ? (
        <EmptyState
          title="No rate cards yet"
          description="Create your first rate card to define billing rates by role and seniority."
          action={<Button onClick={handleCreate}>Create Rate Card</Button>}
        />
      ) : (
        <div className="space-y-3">
          {rateCards.map((rc) => (
            <div
              key={rc._id}
              className={`bg-card border border-border rounded-lg p-4 flex flex-wrap items-center gap-3 ${
                rc._id === activeRateCard?._id ? 'ring-2 ring-emerald-500' : ''
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{rc.code}</span>
                  <Badge className={`${
                    rc.status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                    rc.status === 'draft' ? 'bg-slate-100 text-slate-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {rc.status}
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-800">v{rc.version || '1.0'}</Badge>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {rc.name} · {rc.entries?.length || 0} role(s) · Effective {rc.effectiveFrom ? new Date(rc.effectiveFrom).toLocaleDateString() : 'N/A'}
                  {rc.effectiveTo && <span className="mx-1">→</span>}
                  {rc.effectiveTo && new Date(rc.effectiveTo).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {rc.status === 'draft' ? (
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(rc)} title="Edit Draft">
                    <Edit className="h-4 w-4" />
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground italic px-2">Locked</span>
                )}
                {rc.status === 'draft' && (
                  <Button variant="secondary" size="sm" onClick={() => handleActivate(rc._id, rc.code)}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Confirm / Activate
                  </Button>
                )}
                {rc.status === 'active' && (
                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 border-emerald-300">Active</Badge>
                )}
                {rc.status === 'draft' && (
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => {}} title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal open={creating} onClose={resetForm} title={editingId ? 'Edit Rate Card' : 'Create Rate Card'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Code (e.g. RC-STD-2026) *</label>
              <Input
                placeholder="RC-STD-2026"
                value={formData.code}
                onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                disabled={!!editingId}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Name *</label>
              <Input
                placeholder="Standard 2026 Rate Card"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Description</label>
            <Input
              placeholder="Default rate card for 2026 fiscal year"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Version</label>
              <Input
                placeholder="1.0"
                value={formData.version}
                onChange={e => setFormData({ ...formData, version: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Effective From *</label>
              <Input
                type="date"
                value={formData.effectiveFrom}
                onChange={e => setFormData({ ...formData, effectiveFrom: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Effective To (optional)</label>
              <Input
                type="date"
                placeholder="mm/dd/yyyy"
                value={formData.effectiveTo}
                onChange={e => setFormData({ ...formData, effectiveTo: e.target.value })}
              />
            </div>
          </div>

          {/* Entries */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-slate-600 dark:text-slate-400">Rate Card Entries</label>
              <Button type="button" variant="secondary" size="sm" onClick={addEntry}>
                <Plus className="h-4 w-4 mr-1.5" /> Add Role
              </Button>
            </div>
            <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    {['Role', 'Seniority', 'Cost Rate (₹/hr)', 'Billing Rate (₹/hr)', 'Currency', ''].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {formData.entries.map((e) => (
                    <tr key={e.key} className="bg-white dark:bg-slate-900">
                      <td className="px-4 py-3 w-48">
                        <Select
                          value={e.role}
                          placeholder="Select role"
                          options={ROLE_TYPES.map(r => ({ value: r, label: r }))}
                          onChange={ev => setEntry(e.key, { role: ev.target.value })}
                        />
                      </td>
                      <td className="px-4 py-3 w-32">
                        <Select
                          value={e.seniority}
                          options={SENIORITY_LEVELS.map(s => ({ value: s, label: s }))}
                          onChange={ev => setEntry(e.key, { seniority: ev.target.value })}
                        />
                      </td>
                      <td className="px-4 py-3 w-40">
                        <Input
                          type="number"
                          min="0"
                          step="100"
                          placeholder="0"
                          value={e.hourlyRateMinor}
                          onChange={ev => setEntry(e.key, { hourlyRateMinor: parseInt(ev.target.value) || 0 })}
                        />
                      </td>
                      <td className="px-4 py-3 w-40">
                        <Input
                          type="number"
                          min="0"
                          step="100"
                          placeholder="0"
                          value={e.billingRateMinor}
                          onChange={ev => setEntry(e.key, { billingRateMinor: parseInt(ev.target.value) || 0 })}
                        />
                      </td>
                      <td className="px-4 py-3 w-28">
                        <Select
                          value={e.currency}
                          options={CURRENCIES.map(c => ({ value: c, label: c }))}
                          onChange={ev => setEntry(e.key, { currency: ev.target.value })}
                        />
                      </td>
                      <td className="px-3 py-2 w-10 text-right">
                        <Button variant="ghost" size="icon" onClick={() => removeEntry(e.key)} disabled={formData.entries.length <= 1}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-border pt-4 mt-2">
            <Button type="button" variant="outline" onClick={resetForm} className="px-5 py-2">Cancel</Button>
            <Button type="submit" loading={saving} className="px-5 py-2">
              {editingId ? 'Update' : 'Create'} Rate Card
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}